import { IconComponent } from './components/icon/icon.component';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule, DatePipe, NgOptimizedImage } from '@angular/common';
import { LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import {
  CookieConsentSnackbarComponent,
} from '../core/components/cookie-consent-snackbar/cookie-consent-snackbar.component';
import {
  NewCreditCardDialogEntryComponent,
} from '../credit-card/containers/new-credit-card-dialog-entry/new-credit-card-dialog-entry.component';
import { CloseDueDateInfoComponent } from './components/close-due-date-info/close-due-date-info.component';
import { CreditCardSelectComponent } from './components/credit-card-select/credit-card-select.component';
import { EmailAccountFormComponent } from './components/email-account-form/email-account-form.component';
import { EmptyStateComponent } from './components/empty-state/empty-state.component';
import { ErrorDialogComponent } from './components/error-dialog/error-dialog.component';
import { ErrorStatusComponent } from './components/error-status/error-status.component';
import { FabExtendedComponent } from './components/fab-left/fab-extended.component';
import { FabMultiComponent } from './components/fab-multi/fab-multi.component';
import { FabComponent } from './components/fab/fab.component';
import { FacebookLoginComponent } from './components/facebook-login/facebook-login.component';
import { GoogleLoginComponent } from './components/google-login/google-login.component';
import { HorizontalDividerComponent } from './components/horizontal-divider/horizontal-divider.component';
import { LiabilitiesDelegateComponent } from './components/liabilities-delegate/liabilities-delegate.component';
import { LiabilityStatusComponent } from './components/liability-status/liability-status.component';
import { LoadingComponent } from './components/loading/loading.component';
import { NewCreditCardDialogComponent } from './components/new-credit-card-dialog/new-credit-card-dialog.component';
import { NewLoaderButtonComponent } from './components/new-loader-button/new-loader-button.component';
import { PasswordMeterComponent } from './components/password-meter/password-meter.component';
import { PaymentMethodChooserComponent } from './components/payment-method-chooser/payment-method-chooser.component';

import { PaymentMethodSummaryComponent } from './components/payment-method-summary/payment-method-summary.component';
import { PaymentStatusComponent } from './components/payment-status/payment-status.component';
import { PaymentSummaryComponent } from './components/payment-summary/payment-summary.component';
import { SearchCustomerComponent } from './components/search-customer/search-customer.component';
import { SecondaryToolbarComponent } from './components/secondary-toolbar/secondary-toolbar.component';
import { ShowMoreComponent } from './components/show-more/show-more.component';
import { StatusComponent } from './components/status/status.component';
import { SuccessAnimationComponent } from './components/success-animation/success-animation.component';
import { TagListFilterComponent } from './components/tag-list-filter/tag-list-filter.component';
import { TagListComponent } from './components/tag-list/tag-list.component';
import { ApiCallLoadingDirective } from './directives/api-call-loading.directive';
import { CurrencyDirective } from './directives/currency.directive';
import { GtmFormEventDirective } from './directives/gtm-form-event.directive';
import { InputMaxLengthDirective } from './directives/input-max-length.directive';
import { MaterialModule } from './material.module';
import { AppleLoginComponent } from './components/apple-login/apple-login.component';
import { ExpressPaymentComponent } from './components/express-payment/express-payment.component';
import {
  NotificationsContainerComponent,
} from './components/notifications-container/notifications-container.component';
import {
  NotificationsContainerEmptyStateComponent,
} from './components/notifications-container-empty-state/notifications-container-empty-state.component';
import {
  NotificationsContainerRowComponent,
} from './components/notifications-container-row/notifications-container-row.component';
import {
  NotificationsBillerLogoComponent,
} from './components/notifications-biller-logo/notifications-biller-logo.component';
import {
  AppNotificationsActionsMenuComponent,
} from './components/notifications-actions-menu/notifications-actions-menu.component';
import { BillerLogoComponent } from './components/biller-logo/biller-logo.component';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { NotificationsIconComponent } from './components/notifications-icon/notifications-icon.component';
import { CouponSelectComponent } from './components/coupon-select/coupon-select.component';
import { CouponSelectRowComponent } from './components/coupon-select-row/coupon-select-row.component';
import { StepperProgressBarComponent } from './components/stepper-progress-bar/stepper-progress-bar.component';
import { EmptyCartComponent } from './components/empty-cart/empty-cart.component';
import {
  NewCustomerBillerFromEmptyCartTimeoutDialogComponent,
} from './components/new-customer-biller-from-empty-cart-timeout-dialog/new-customer-biller-from-empty-cart-timeout-dialog.component';
import { DocumentPreviewComponent } from './components/document-preview/document-preview.component';
import { CartButtonComponent } from './components/cart-button/cart-button.component';
import {
  ErrorRefreshingTokenDialogComponent,
} from './components/error-refreshing-token-dialog/error-refreshing-token-dialog.component';
import {
  TwistoUnavailableDialogComponent,
} from '../pay/components/twisto-unavailable-dialog/twisto-unavailable-dialog.component';
import {
  TopUpDisabledBlockPaymentDialogComponent,
} from './components/topup-disabled-block-payment-dialog/topup-disabled-block-payment-dialog.component';
import { OneLoginComponent } from './components/one-login/one-login.component';
import { TopupIconComponent } from './components/topup-icon/topup-icon.component';
import {
  LiabilityPaymentReclassifiedToPreviousComponent,
} from './components/liability-payment-transferred-to-previous/liability-payment-reclassified-to-previous.component';
import {
  DisabledExpressPaymentDialogComponent,
} from './components/disabled-express-payment-dialog/disabled-express-payment-dialog.component';
import { SliderComponent } from './components/slider/slider.component';
import { AmountTileComponent } from './components/amount-tile/amount-tile.component';
import { TableComponent } from './components/table/table.component';
import { MatTableModule } from '@angular/material/table';
import { DesktopTableComponent } from './components/table/desktop-table/desktop.table.component';
import { MobileTableComponent } from './components/table/mobile-table/mobile.table.component';
import { TileButtonComponent } from './components/tile-button/tile-button.component';
import { ExpandedDetailComponent } from './components/table/expanded-detail/expanded-detail.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { BbanPipe } from './utils/pipes/bban.pipe';
import { SafeHtmlPipe } from './utils/pipes/safe-html.pipe';
import { PaykaCurrencyPipe } from './utils/pipes/payka-currency.pipe';
import {
  LiabilityExpandableMenuComponent,
} from './components/table/liability-expandable-menu/liability-expandable-menu.component';
import { CardComponent } from './components/container-with-card/card.component';
import { CodeComponent } from './components/code/code.component';
import { QrCodeComponent } from 'ng-qrcode';
import { KontomatikComponent } from './components/kontomatik/kontomatik.component';
import { DialogMsgComponent } from './components/dialog-msg/dialog-msg.component';
import { DateToPipe } from './utils/pipes/date-to.pipe';
import { ExpandedComponentDirective } from './directives/expanded-component.directive';
import { ExpandedRowComponent } from './components/table/expanded-row/expanded-row.component';
import { NewLoadingComponent } from './components/new-loading/new-loading.component';
import { NewSpinnerComponent } from './components/new-spinner/new-spinner.component';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { PhoneFormatPipe } from './pipes/phone-format.pipe';
import { NewSliderComponent } from './components/new-slider/new-slider.component';
import { InputDateMaskDirective } from './directives/input-date-mask.directive';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import {
  DefaultClassDirective,
  DefaultFlexAlignDirective,
  DefaultFlexDirective,
  DefaultLayoutAlignDirective,
  DefaultLayoutDirective,
  DefaultLayoutGapDirective,
  DefaultShowHideDirective,
  DefaultStyleDirective,
} from 'ng-flex-layout';
import { ShareButton } from 'ngx-sharebuttons/button';
import { CarouselComponent } from './components/carousel/carousel.component';
import { ContainerComponent } from './components/container/container.component';
import { AvatarComponent } from './components/avatar/avatar.component';
import { MatPaginatorModule } from '@angular/material/paginator';


@NgModule({
  declarations: [
    AmountTileComponent,
    NewLoadingComponent,
    ErrorDialogComponent,
    ErrorRefreshingTokenDialogComponent,
    ErrorStatusComponent,
    FabExtendedComponent,
    SuccessAnimationComponent,
    StatusComponent,
    LiabilityStatusComponent,
    CookieConsentSnackbarComponent,
    NewLoaderButtonComponent,
    PasswordMeterComponent,
    ShowMoreComponent,
    FabMultiComponent,
    EmailAccountFormComponent,
    PaymentStatusComponent,
    NewCreditCardDialogEntryComponent,
    SearchCustomerComponent,
    CreditCardSelectComponent,
    FacebookLoginComponent,
    GoogleLoginComponent,
    HorizontalDividerComponent,
    PaymentSummaryComponent,
    PaymentStatusComponent,
    PaymentMethodSummaryComponent,
    PaymentMethodChooserComponent,
    CloseDueDateInfoComponent,
    NewCreditCardDialogComponent,
    LiabilitiesDelegateComponent,
    AppleLoginComponent,
    ExpressPaymentComponent,
    NotificationsContainerComponent,
    NotificationsContainerEmptyStateComponent,
    NotificationsContainerRowComponent,
    NotificationsBillerLogoComponent,
    AppNotificationsActionsMenuComponent,
    BillerLogoComponent,
    NotificationsIconComponent,
    CouponSelectComponent,
    CouponSelectRowComponent,
    StepperProgressBarComponent,
    EmptyCartComponent,
    NewCustomerBillerFromEmptyCartTimeoutDialogComponent,
    DocumentPreviewComponent,
    CartButtonComponent,
    TwistoUnavailableDialogComponent,
    TopUpDisabledBlockPaymentDialogComponent,
    OneLoginComponent,
    LiabilityPaymentReclassifiedToPreviousComponent,
    TopupIconComponent,
    DisabledExpressPaymentDialogComponent,
    SliderComponent,
    CarouselComponent,
    NewSliderComponent,
    AmountTileComponent,
    TableComponent,
    CardComponent,
    CodeComponent,
    KontomatikComponent,
    DialogMsgComponent,

  ],
  imports: [
    MaterialModule,
    TileButtonComponent,
    MatPaginatorModule,
    DefaultClassDirective,
    ShareButton,
    DefaultFlexAlignDirective,
    DefaultStyleDirective,
    DefaultFlexDirective,
    DefaultLayoutDirective,
    DefaultLayoutAlignDirective,
    DefaultLayoutGapDirective,
    DefaultShowHideDirective,
    QrCodeComponent,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    OverlayModule,
    InfiniteScrollModule,
    MatTableModule,
    MatAutocompleteModule,
    NewSpinnerComponent,
    NgxExtendedPdfViewerModule,
    NgxMaskDirective, NgxMaskPipe,
    EmptyStateComponent,
    LoadingComponent,
    FabComponent,
    SecondaryToolbarComponent,
    ApiCallLoadingDirective,
    TagListFilterComponent,
    TagListComponent,
    IconComponent,
    DesktopTableComponent,
    MobileTableComponent,
    DateToPipe, BbanPipe,
    SafeHtmlPipe,
    PaykaCurrencyPipe,
    PhoneFormatPipe,
    GtmFormEventDirective,
    InputMaxLengthDirective,
    InputDateMaskDirective,
    CurrencyDirective,
    ExpandedComponentDirective,
    NgOptimizedImage,
    ContainerComponent,
    ExpandedDetailComponent,
    LiabilityExpandableMenuComponent,
    ExpandedRowComponent, AvatarComponent,
  ],
  providers: [
    provideNgxMask(),
    DatePipe,
    { provide: LOCALE_ID, useValue: 'pl' },

  ],
  exports: [
    MaterialModule,
    ReactiveFormsModule,
    FormsModule,
    MatTableModule,
    LoadingComponent,
    NewSpinnerComponent,
    NewLoadingComponent,
    EmptyStateComponent,
    ErrorStatusComponent,
    MatPaginatorModule,
    FabComponent,
    FabExtendedComponent,
    CurrencyDirective,
    SuccessAnimationComponent,
    StatusComponent,
    LiabilityStatusComponent,
    SecondaryToolbarComponent,
    NewLoaderButtonComponent,
    AmountTileComponent,
    PasswordMeterComponent,
    ShowMoreComponent,
    FabMultiComponent,
    EmailAccountFormComponent,
    PaymentStatusComponent,
    NewCreditCardDialogEntryComponent,
    SearchCustomerComponent,
    ApiCallLoadingDirective,
    ExpandedComponentDirective,
    CreditCardSelectComponent,
    GtmFormEventDirective,
    TagListFilterComponent,
    TagListComponent,
    InputMaxLengthDirective,
    InputDateMaskDirective,
    FacebookLoginComponent,
    GoogleLoginComponent,
    HorizontalDividerComponent,
    PaymentSummaryComponent,
    PaymentStatusComponent,
    PaymentMethodSummaryComponent,
    PaymentMethodChooserComponent,
    CloseDueDateInfoComponent,
    LiabilitiesDelegateComponent,
    IconComponent,
    AppleLoginComponent,
    NotificationsContainerComponent,
    NotificationsContainerEmptyStateComponent,
    NotificationsContainerRowComponent,
    NotificationsBillerLogoComponent,
    AppNotificationsActionsMenuComponent,
    StepperProgressBarComponent,
    BillerLogoComponent,
    DocumentPreviewComponent,
    CartButtonComponent,
    TwistoUnavailableDialogComponent,
    OneLoginComponent,
    LiabilityPaymentReclassifiedToPreviousComponent,
    TopupIconComponent,
    NewSpinnerComponent,
    TableComponent,
    DesktopTableComponent,
    MobileTableComponent,
    SliderComponent,
    NewSliderComponent,
    TileButtonComponent,
    BbanPipe,
    SafeHtmlPipe,
    PaykaCurrencyPipe,
    DateToPipe,
    CardComponent,
    CodeComponent,
    KontomatikComponent,
    ExpandedDetailComponent,
    LiabilityExpandableMenuComponent,
    ExpandedRowComponent,
    CarouselComponent,
    DefaultClassDirective,
    DefaultFlexDirective,
    DefaultLayoutDirective,
    DefaultLayoutAlignDirective,
    DefaultLayoutGapDirective,
    DefaultShowHideDirective,
    DefaultStyleDirective,
    DefaultFlexAlignDirective,
    ShareButton,
    QrCodeComponent,
    DateToPipe, BbanPipe,
    SafeHtmlPipe,
    PaykaCurrencyPipe,
    PhoneFormatPipe,
  ],
})
export class SharedModule {
}
